import { type ColorName, useTheme } from '@emotion/react';
import * as Graph from 'components/graph';
import { extent } from 'd3-array';
import { fadeColor } from 'design-system/theme';
import { integerFormatter } from 'helpers';
import type React from 'react';

import { Tooltip } from './Tooltip';
import type { GradientPoint } from './types';
import { getTooltipData } from './useTooltipProps';

export interface SecondaryData {
  title: string;
  identifier: string;
  data: Graph.Datum[];
  color: ColorName;
  gradient?: GradientPoint[];
  unit?: string;
  showTooltip?: boolean;
  simpleTooltip?: boolean;
}

interface SecondaryDisplayProps extends SecondaryData {
  valueFormatter?: (value?: number) => string;
  hover?: Graph.Datum;
  isSelected?: boolean;
}

export const SecondaryDisplay: React.FC<SecondaryDisplayProps> = ({
  gradient = [],
  identifier,
  color,
  data,
  hover,
  unit,
  showTooltip = false,
  simpleTooltip = false,
  valueFormatter = integerFormatter,
  isSelected,
}) => {
  const theme = useTheme();

  const { closest, tooltipText } = getTooltipData({
    hover,
    unit,
    valueFormatter,
    data,
    simple: simpleTooltip,
  });

  const secondaryLinearGradientId = `secondary-gradient-${identifier}-${Math.random()}`;

  // if there is no gradient this needs to be undefined
  const secondaryLineGradientId = gradient.length
    ? secondaryLinearGradientId
    : undefined;

  const secondaryDomain = Graph.getFullDomain(
    extent(data, (datum) => datum?.y),
  );

  const baseColor = theme.color[color];

  const resolvedColor = fadeColor(baseColor, isSelected ? 1 : 0.2);

  const stops = gradient.map<Graph.StopProps>((stop) => ({
    offsetVal: stop.value,
    color: stop.color ? theme.color[stop.color] : resolvedColor,
  }));

  const hasTooltip = closest && showTooltip;

  return (
    <>
      {gradient.length ? (
        <Graph.LinearGradient
          relativeDomain={secondaryDomain}
          id={secondaryLinearGradientId}
          direction="vertical"
          stops={stops}
        />
      ) : null}
      <Graph.PlotLine
        data={data}
        stroke={resolvedColor}
        strokeWidth={2}
        gradientId={secondaryLineGradientId}
        stops={stops}
      />
      {hasTooltip ? (
        <Tooltip
          baseColor={resolvedColor}
          closest={closest}
          stops={stops}
          tooltipText={tooltipText}
        />
      ) : null}
    </>
  );
};
